import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import { GridColDef, GridRowId, GridRowModel } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  IFacility,
  IFacilityProject,
  IPage,
} from 'common/src/types/interfaces';

import { getFacilities } from 'portal/src/api/FacilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';
import addDateColumnProps from 'portal/src/components/common/tables/addDateColumnProps';
import ConfirmDeleteDialog from 'portal/src/components/common/tables/ConfirmDeleteDialog';
import { getStartAndEndDateInRowValidation } from 'portal/src/components/common/tables/inRowValidators';

import FacilityFormDialog from './FacilityFormDialog';

const InitialFacilities = ({
  data,
  onDataChange,
}: {
  data: IFacilityProject[];
  onDataChange: (data: any[]) => void;
}) => {
  const { hasReadOnlyAccess } = useAuth();
  const { data: facilities } = useQuery({
    queryKey: ['facilities'],
    queryFn: async () => await getFacilities(),
    initialData: { data: [] } as IPage<IFacility>,
  });
  const isReadOnly = hasReadOnlyAccess();

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<GridRowId>();
  const [isFacilityFormOpen, setIsFacilityFormOpen] = useState(false);
  const startAndEndDateValidation = getStartAndEndDateInRowValidation();

  const selectedFacility =
    selectedRowId !== undefined
      ? data.find((facility, index) => {
          return (
            facility.id === selectedRowId ||
            facility.facilityId === selectedRowId ||
            index === selectedRowId
          );
        })
      : undefined;

  const findSelectedFacilityIndex = () => {
    if (selectedRowId === undefined) {
      return -1;
    }

    return data.findIndex((facility, index) => {
      return (
        facility.id === selectedRowId ||
        facility.facilityId === selectedRowId ||
        index === selectedRowId
      );
    });
  };

  const processRowUpdate = (updatedRow: GridRowModel) => {
    onDataChange(
      data.map((facility) => {
        if (
          facility.id === updatedRow.id ||
          facility.facilityId === updatedRow.id
        ) {
          return {
            ...facility,
            ...updatedRow,
          };
        }

        return facility;
      }),
    );

    return updatedRow;
  };

  const handleOpenAddFacilityForm = () => {
    setSelectedRowId(undefined);
    setIsFacilityFormOpen(true);
  };

  const handleCloseAddFacilityForm = () => {
    setIsFacilityFormOpen(false);
    setSelectedRowId(undefined);
  };

  const handleSaveFacility = (newFacility) => {
    onDataChange([...data, newFacility]);
    handleCloseAddFacilityForm();
  };

  const handleEditFacility = (editedFacility) => {
    const selectedFacilityIndex = findSelectedFacilityIndex();

    if (selectedFacilityIndex !== -1) {
      onDataChange(
        data.map((facility, index) => {
          if (index === selectedFacilityIndex) {
            return {
              ...facility,
              ...editedFacility,
            };
          }

          return facility;
        }),
      );
    }

    handleCloseAddFacilityForm();
  };

  const handleRemoveFacility = () => {
    if (selectedRowId !== undefined) {
      onDataChange(
        data.filter((facility, index) => {
          return !(
            facility.id === selectedRowId ||
            facility.facilityId === selectedRowId ||
            index === selectedRowId
          );
        }),
      );
    }

    setSelectedRowId(undefined);
    setOpenDeleteDialog(false);
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'facility',
      headerName: 'Facility Name',
      minWidth: 256,
      flex: 1,
      valueGetter: (value: IFacility, row) =>
        value?.name ??
        facilities.data?.find((f) => f.id === row.facilityId)?.name ??
        '',
    },
    {
      field: 'startDate',
      editable: true,
      ...startAndEndDateValidation.startDate,
      ...addDateColumnProps('Start Date', 200),
    },
    {
      field: 'endDate',
      editable: true,
      ...startAndEndDateValidation.endDate,
      ...addDateColumnProps('End Date', 200),
    },
  ];

  return (
    <>
      <DataTable
        actionsConfig={{
          inRowEditConfig: {
            processRowUpdate,
            btnConfig: (row) => ({
              overrideEditBtn: () => {
                setSelectedRowId(row.id);
                setIsFacilityFormOpen(true);
              },
              onDeleteClick: () => {
                setSelectedRowId(row.id);
                setOpenDeleteDialog(true);
              },
            }),
          },
        }}
        columns={gridColumns}
        checkboxSelection={false}
        getRowId={(row) => row.id ?? row.facilityId}
        isRowNavigation={false}
        onRowSelectionModelChange={({ ids }) => setSelectedRowId([...ids][0])}
        rows={
          data.length === 0
            ? []
            : data.map((facility) => ({
                ...facility,
                id: facility.id ?? facility.facilityId,
              }))
        }
        toolbarSlotProps={{
          children: (
            <Button
              color="primary"
              data-testid="add-facility-table-btn"
              disabled={isReadOnly}
              startIcon={<AddIcon />}
              onClick={handleOpenAddFacilityForm}
              size="small"
              variant="outlined"
            >
              Add Facility
            </Button>
          ),
        }}
      />
      {isFacilityFormOpen && (
        <FacilityFormDialog
          entity={selectedFacility}
          facilities={facilities.data}
          open={isFacilityFormOpen}
          onClose={handleCloseAddFacilityForm}
          onSave={
            selectedRowId !== undefined
              ? handleEditFacility
              : handleSaveFacility
          }
        />
      )}
      <ConfirmDeleteDialog
        showDialog={openDeleteDialog}
        itemName="facility"
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleRemoveFacility}
        isLoading={false}
      />
    </>
  );
};

export default InitialFacilities;
